import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Type, inject } from '@angular/core';

import {
    Accessory,
    Delivery,
    Equipment,
    Fai,
    Ideo,
    Plan,
    Product,
    ProductFactory,
    PromotionModel,
    QUOTE_CONTEXTS,
    QuoteContextAcquisitionModel,
    QuoteContextModel,
    QuoteModel,
    Sensation,
    Sim,
    Simo,
    Sowo,
    Subscription,
} from '@bytel/bytel-sales';
import {
    CartCheckoutService,
    CheckoutCartModel,
    CheckoutStorageService,
    DeliveryInfo,
    DeliveryService,
    FUNDING_ENUM,
    FundingMethod,
    FundingService,
    QuoteContextFactory,
    UserService,
    YAMA_STORAGE_KEY,
} from '@bytel/checkout';

import { SIM_TYPES } from '@interfaces/sim.interace';

import { Observable, ReplaySubject, from, lastValueFrom, mergeMap, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Oauth2ResourcesService } from '../common/public-api';
import { BasicObject, IProductAction } from '../interfaces/checkout.interface';
import { ConfigurationService } from './configuration.service';
import { SimService } from './sim.service';

// @dynamic
@Injectable({
    providedIn: 'root',
})
export class MainCartService {
    public cartLoadedFromStorageObs: Observable<BasicObject>;
    public cartModel: CheckoutCartModel;
    public cartQuoteSubscription: ReplaySubject<number>;
    public cartRefreshObs: Observable<CartCheckoutService>;
    public couponObs: Observable<void>;
    public paymentMethodObs: Observable<BasicObject>;
    public productActionObs: Observable<IProductAction>;

    protected simService: SimService = inject(SimService);
    protected fundingService: FundingService = inject(FundingService);
    constructor(
        protected userService: UserService,
        private cartCheckoutService: CartCheckoutService,
        private quoteContextFactory: QuoteContextFactory,
        private deliveryService: DeliveryService,
        private checkoutStorageService: CheckoutStorageService,
        private oauthService: Oauth2ResourcesService,
        private config: ConfigurationService,
        @Inject(DOCUMENT) private document: Document,
    ) {
        this.cartModel = new CheckoutCartModel();
        this.cartCheckoutService.initCart(this.cartModel);
        this.cartQuoteSubscription = this.cartCheckoutService.cartQuoteSubscription;
        this.cartRefreshObs = this.cartCheckoutService.cartRefreshObs;
        this.cartLoadedFromStorageObs = this.cartCheckoutService.cartLoadedFromStorageObs;
        this.productActionObs = this.cartCheckoutService.productActionObs;
        this.paymentMethodObs = this.cartCheckoutService.paymentMethodObs;
        this.couponObs = this.cartCheckoutService.couponActionObs;
    }

    public addManualPromotion(manualPromotion: PromotionModel): Observable<void> {
        return this.cartCheckoutService.addManualPromotion(this.cartModel, manualPromotion);
    }

    public addProduct(
        product: Product,
        quoteIndex: number = this.cartModel.currentQuoteIndex,
        autoRefresh: boolean = true,
    ): Observable<Product> {
        return this.cartCheckoutService.addProduct(this.cartModel, product, quoteIndex, autoRefresh);
    }

    public addProductByGencode(
        gencode: string,
        quoteIndex: number = 0,
        autoRefresh: boolean = true,
    ): Observable<Product> {
        return this.cartCheckoutService.addProductByGencode(this.cartModel, gencode, quoteIndex, autoRefresh);
    }

    public addProductsByAttributes(values: string[], attribute): Observable<Product[]> {
        return this.cartCheckoutService.addProductsByAttributes(this.cartModel, values, attribute);
    }

    public addProductsByGencodes(gencodes: string[], quoteIndex: number = 0): Observable<Product[]> {
        return this.cartCheckoutService.addProductsByGencodes(this.cartModel, gencodes, quoteIndex);
    }

    public addProducts(gencodes: string[], isFai: boolean = false): Observable<CheckoutCartModel> {
        return this.cartCheckoutService.addProductsToCart(gencodes, this.cartModel, isFai);
    }

    public addQuote(context: QuoteContextModel = new QuoteContextAcquisitionModel()): number {
        this.cartModel.quotes = this.cartModel.quotes.filter((quote) => quote.products.length !== 0);

        const quoteIndex = this.cartModel.quotes.findIndex((quote) => quote.context.id === context.id);
        if (quoteIndex !== -1) {
            return quoteIndex;
        }
        const newQuoteIndex = this.cartCheckoutService.addQuote(this.cartModel, context);
        this.cartCheckoutService.setFundingMethod(this.cartModel, null);
        return newQuoteIndex;
    }

    public deleteCartIds(): void {
        this.cartCheckoutService.deleteCartIds(this.cartModel);
    }

    public getAllProductsByType<T extends Product>(myClass: string): T[] {
        return this.cartCheckoutService.getAllProductsByType(this.cartModel, myClass);
    }

    public getDelivery(quoteIndex: number = this.cartModel.currentQuoteIndex): DeliveryInfo {
        return this.deliveryService.getDelivery(this.cartCheckoutService, this.cartModel, quoteIndex);
    }

    public getProductQty(product: Product): number {
        let qty = 0;
        this.cartModel.quotes.forEach((quote) => {
            qty += quote.getProductsByGencode(product.gencode).length;
        });

        return qty;
    }

    public getQuote(quoteIndex: number = 0): QuoteModel {
        return this.cartCheckoutService.getQuote(this.cartModel, quoteIndex);
    }

    public getQuoteIndexByContext(quoteContext: QUOTE_CONTEXTS): number {
        const index = this.cartModel.quotes.findIndex((quote) => quote.context.id === quoteContext);
        return index !== -1 ? index : undefined;
    }

    public hasOnlyAccessories(quote: QuoteModel): boolean {
        const subscription: Subscription = quote.getPrincipalProduct<Subscription>('Subscription');
        const equipment: Equipment = quote.getPrincipalProduct<Equipment>('Equipment');
        const accesories: Accessory[] = quote.getProductsByType<Accessory>('Accessory');

        return !!(!equipment && !subscription && accesories?.length);
    }

    public isMigratingPlan(): boolean {
        // for graphql version: const currentPlanId: string = this.userService?.user.defaultContract?.abonnement?.idOffre;
        const currentPlanId: string = this.userService?.user.defaultContract?.abonnement?._links?.offre?.href
            .replace(/\D/g, '')
            .trim();
        const quotePlan: string = this.cartCheckoutService.getQuote(this.cartModel).getPrincipalProduct<Plan>('Plan')
            ?.data?.gencode;
        if (!quotePlan || !currentPlanId) {
            return false;
        }

        return quotePlan !== currentPlanId;
    }

    public isBankCart(): boolean {
        return this.cartModel.isBanking();
    }

    public crossSellActiveForFAMOnCheckout(): boolean {
        return this.config.getData()?.global?.crossSellActiveForFAMOnCheckout;
    }

    public isPreCrossSell(quoteIndex: number = 0): boolean {
        if (!this.cartModel.quotes.length || this.cartModel.quotes.length > 1) {
            return false;
        }

        const quote: QuoteModel = this.getQuote(quoteIndex);

        const plan: Plan = quote.getPrincipalProduct<Plan>('Plan');

        const preCrossSellPlans: (typeof Plan)[] = [Sowo, Ideo];

        if (!this.crossSellActiveForFAMOnCheckout()) {
            preCrossSellPlans.push(Sensation);
        }

        return preCrossSellPlans.some((p) => ProductFactory.Is<Plan>(plan, p, true));
    }

    /**
     * Check if plan is sowo (byou)
     * @returns true / false
     */
    public isSowo(): boolean {
        const quote = this.getQuote();
        const plan = quote.getPrincipalProduct<Subscription>('Subscription');
        return ProductFactory.Is(plan, Sowo, true);
    }

    /**
     * Check if plan is simo
     * @returns true / false
     */
    public isSimo(): boolean {
        const quote = this.getQuote();
        const plan = quote.getPrincipalProduct<Subscription>('Subscription');
        return ProductFactory.Is(plan, Simo, true);
    }

    public isSowoOrSimo(quoteIndex: number = this.cartModel.currentQuoteIndex): boolean {
        const quote = this.getQuote(quoteIndex);
        const plan = quote.getPrincipalProduct<Subscription>('Subscription');
        return ProductFactory.Is(plan, Sowo, true) || ProductFactory.Is(plan, Simo, true);
    }

    /**
     * Check if cart has only accessories
     * @returns boolean
     */
    public isAccessoriesOnly(): boolean {
        const quote = this.getQuote();
        return quote.products.every((p) => ProductFactory.Is(p, Accessory) || ProductFactory.Is(p, Delivery));
    }

    public refresh(): Observable<void> {
        return from(this.cartCheckoutService.refresh(this.cartModel));
    }

    public refreshCartIsPro(): void {
        this.cartCheckoutService.refreshCartIsPro(this.cartModel);
    }

    /**
     * Call api to remove coupon
     * @returns number
     */
    public removeCoupon(): Observable<number> {
        return this.cartCheckoutService.removeCoupon(this.cartModel);
    }

    public removeCouponPromotion(): Observable<void> {
        return this.cartCheckoutService.removeCouponPromotion(this.cartModel);
    }

    public removeManualPromotion(promo: PromotionModel): Observable<void> {
        return this.cartCheckoutService.removeManualPromotion(this.cartModel, promo);
    }

    public removeProduct(
        product: Product,
        quoteIndex: number = this.cartModel.currentQuoteIndex,
        autoRefresh: boolean = true,
    ): Observable<Product> {
        let productToRemove = null;

        return this.updatePromoConfigurator(product).pipe(
            mergeMap(() => this.cartCheckoutService.removeProduct(this.cartModel, product, quoteIndex, autoRefresh)),
            mergeMap((removedProduct) => {
                productToRemove = removedProduct;
                if (this.cartModel.quotes.length > 1 && this.cartModel.quotes[quoteIndex].products.length === 0) {
                    if (this.cartModel.quotes[quoteIndex].id) {
                        this.deleteQuote(this.cartModel.cartId, this.cartModel.quotes[quoteIndex].id).subscribe();
                    }
                    this.cartModel.quotes.splice(quoteIndex, 1);

                    return this.refresh();
                }
                return of(null);
            }),
            map(() => productToRemove),
        );
    }

    public removeProductsByGencode(gencode: string, quoteIndex: number = 0): Observable<Product[]> {
        return this.cartCheckoutService.removeProductsByGencode(this.cartModel, gencode, quoteIndex);
    }

    public removeQuote(quoteIndex: number = this.cartModel.currentQuoteIndex): Observable<void> {
        return this.cartCheckoutService.removeQuote(this.cartModel, quoteIndex);
    }

    public reset(): void {
        this.cartModel = new CheckoutCartModel();
        this.cartCheckoutService.refreshType(this.cartModel);
    }

    public resetAll(): void {
        this.reset();
        this.cartCheckoutService.resetAll();
    }

    public resolve(): Observable<void> {
        return from(this.cartCheckoutService.resolve(this.cartModel));
    }

    public save(): void {
        return this.cartCheckoutService.save(this.cartModel);
    }

    public setCouponPromotion(promo: PromotionModel): Observable<void> {
        return this.cartCheckoutService.setCouponPromotion(this.cartModel, promo);
    }

    public setDelivery(
        deliveryInfo: DeliveryInfo,
        quoteIndex: number = this.cartModel.currentQuoteIndex,
    ): Observable<Product> {
        return this.deliveryService.setDelivery(this.cartCheckoutService, this.cartModel, deliveryInfo, quoteIndex);
    }

    public setFundingMethod(fundingMethod: FundingMethod): Observable<boolean> {
        return this.cartCheckoutService.setFundingMethod(this.cartModel, fundingMethod);
    }

    public setIsFromProWall(isProWall: boolean): void {
        this.cartCheckoutService.setIsFromProWall(this.cartModel, isProWall);
    }

    public setPro(isPro: boolean = true): void {
        this.cartCheckoutService.setPro(this.cartModel, isPro);
    }

    // specific for main cart service
    public addQuoteWithContext<T extends QuoteContextModel>(
        contextType: Type<T>,
        params: BasicObject = {},
    ): Observable<number> {
        return this.quoteContextFactory
            .createContext<T>(contextType, params)
            .pipe(map((context) => this.addQuote(context)));
    }

    public updateProductQty(
        product: Product,
        qty: number,
        quoteIndex: number = this.cartModel.currentQuoteIndex,
    ): Observable<Product[]> {
        return this.cartCheckoutService.updateProductQty(this.cartModel, product, qty, quoteIndex);
    }

    public removeRedirectBackdrop(): void {
        const element = this.document.getElementById('backdrop-loader');
        if (element) {
            element.remove();
        }
    }

    /**
     * check if stored appointment exist and is valid for FTTH only
     * @returns boolean
     */
    public isAppointmentValid(): boolean {
        const plan = this.getQuote().getPrincipalProduct<Plan>('Plan');
        if (ProductFactory.Is(plan, Fai) && plan.data.technology === Fai.TECHNOLOGIES.FTTH) {
            const appointment = this.getQuote().forms.appointment;
            return appointment && appointment.expiresTimestamp > new Date().getTime();
        }
        return true;
    }

    /**
     * Retrieve fai quote
     * @returns QuoteModel
     */
    public getQuoteFai(): QuoteModel {
        return this.cartModel.quotes.find((quote) => quote.isAcquisitionFix());
    }

    public hasBankingOffer(): boolean {
        const quote = this.cartModel.getQuote();
        if (quote) {
            return quote.getPrincipalProduct<Plan>('Plan')?.data.banking_offer;
        }
        return false;
    }

    public isBankingCartExpired(): boolean {
        const yama_cart = this.checkoutStorageService.get(YAMA_STORAGE_KEY, false);
        if (!yama_cart && this.hasBankingOffer()) {
            return true;
        }

        return false;
    }

    public isFTTH(): boolean {
        const quote = this.getQuoteFai();
        const plan = quote?.getPrincipalProduct<Plan>('Plan');
        return ProductFactory.Is(plan, Fai, true) && plan.data.technology === Fai.TECHNOLOGIES.FTTH;
    }

    /**
     * Check if quote hasEsim / has only esim
     * @param quoteIndex current quote index
     * @param hasOnly check if quote has only eSIM if true
     * @returns boolean
     */
    public quoteHasEsim(quoteIndex: number, hasOnly: boolean = false): boolean {
        const quote = this.getQuote(quoteIndex);
        if (hasOnly) {
            return quote.products.every((p) => ProductFactory.Is(p, Accessory) || ProductFactory.Is(p, Delivery));
        } else {
            const sim = quote.getProductByType(Sim);
            if (sim && sim.data.type_id === SIM_TYPES.QRESIM) {
                return true;
            }
            return false;
        }
    }

    /**
     * Check if cart contain eSIM
     */
    public cartHasEsim(): boolean {
        return this.cartModel.quotes.some((quote) => {
            const sim = quote.getProductByType(Sim);
            return sim && sim.data.type_id === SIM_TYPES.QRESIM;
        });
    }

    public cartHasProductByType(myClass: string): boolean {
        const products = this.cartModel.quotes.flatMap((quote) => quote.products);
        return products.some((p) => ProductFactory.Is(p, myClass));
    }

    public async updateSim(quoteIndex: number, product: Product): Promise<void> {
        await this.simService.updateSim(this.cartModel, quoteIndex, product);
        await lastValueFrom(this.refresh());
    }

    public async updateSimPrices(quoteIndex: number): Promise<void> {
        this.simService.updateSimPrices(this.cartModel, quoteIndex);
        await lastValueFrom(this.refresh());
        await this.cartCheckoutService.updateFunding(this.cartModel, quoteIndex);
    }

    public isSwappyCart(): boolean {
        return this.cartModel.quotes.some((quote) => quote.products.some((product) => ProductFactory.Is(product, Plan) && product.data.sub_type_id === 'swappy'));
    }

    public async checkSwappyValidity(): Promise<boolean> {
        const fundingCart = await lastValueFrom(this.fundingService.getVirtualFundingModes(this.cartModel));
        const swappyFundingMethod = fundingCart?.fundingMethods.find(funding => funding.type === FUNDING_ENUM.SWAPPY);

        if (!swappyFundingMethod) {
            return false;
        }

        await lastValueFrom(this.setFundingMethod(swappyFundingMethod));
        await lastValueFrom(this.refresh());
        return true;
    }

    private deleteQuote(cartId: number, quoteId: number): Observable<boolean> {
        return this.oauthService
            .ventes()
            .panier(cartId)
            .parcours(quoteId)
            .setLocalService()
            .useSalesApi()
            .delete()
            .pipe(
                catchError((error) => {
                    console.log(error);
                    return of(true);
                }),
                map(() => true),
            );
    }

    private updatePromoConfigurator(removedProduct: Product): Observable<void> {
        const promoConfig = this.checkoutStorageService.get('promoConfigurator', false);

        if (!removedProduct || !promoConfig) {
            return of(null);
        }

        if (removedProduct.productLine === 'Forfaits_mobile') {
            promoConfig.mobile = false;
        } else if (removedProduct.productLine === 'FAI') {
            promoConfig.home = false;
        }

        sessionStorage.setItem('promoConfigurator', JSON.stringify(promoConfig));

        return of(null);
    }
}
